import React, {
  useCallback,
  useRef,
  useEffect,
  useState,
  useMemo,
} from 'react';
import styled from 'styled-components/macro';

import formatDate, { PeriodKey } from 'helpers/formatDate';
import formatNumber, { FormatType } from 'helpers/formatNumber';
import getTimeAxisConfiguration from './helpers/getTimeAxisConfiguration';
import {
  primaryTypography,
  numbersTypography,
} from 'styles/global/typography/typography';
import { Label } from './shared.style';

const TickText = styled(Label)`
  text-transform: none;
`;

const YTickText = styled(TickText)`
  ${numbersTypography};
`;

const XTickText = styled(TickText)`
  ${primaryTypography};
`;

const AxisLabel = styled(Label)<any>`
  ${numbersTypography};
  transform: ${({ isStrictDesign }): string =>
    isStrictDesign ? 'rotate(270deg)' : ''};
  @media screen and (max-width: 40em) {
    font-size: 0.5625em;
  }
`;

const Axis = styled('g')`
  path,
  line {
    stroke: ${({ theme }): string => theme.Grey};
  }
`;

const HorizontalAxis = styled(Axis)`
  g:last-of-type:not(:only-of-type) ${XTickText} {
    text-anchor: end;
  }
`;

const VerticalAxis = styled(Axis)<any>`
  overflow: hidden;
  display: ${({ isHidden }): string => (isHidden ? 'none' : 'block')};

  path,
  line {
    stroke: currentColor;
    stroke-dasharray: 1, 5;
  }
  & > g > text {
    font-family: Proxima Nova;
  }
`;

const AxisBaseLine = styled('line')`
  stroke: ${({ theme }): string => theme.Grey};
`;

interface IAxesInStrictDesignProps {
  /** A d3-scale linear time scale */
  x: any;
  /** Are we on mobile? */
  isSmallScreen: boolean;
  /** Day, week, month or year */
  period: PeriodKey;
  /**
   * The number of dataPoints there are on the X-scale.
   * This can vary because we trim the charts so that they only show actual values.
   * We use this to calculate the number of ticks. */
  numberOfDataPoints: number;
  /** The margin configuration object from LineChart */
  margin: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  /** Used to define several sizes. Defined in SVG coordinates (ie. unitless). */
  sizes: {
    width: number;
    height: number;
  };
  /** Do we have any values in the data to base the axes on? */
  hasNoData: boolean;
  /** Option to show labels on the y axis */
  dataType: FormatType;
  /** MetricType is a view type: views or creations */
  metricType: string;
  /** Trend entities with path, gradient, y */
  metricEntities: any;
}

const AxesInStrictDesign: React.FC<IAxesInStrictDesignProps> = (props) => {
  const {
    x,
    isSmallScreen,
    period,
    numberOfDataPoints,
    margin,
    sizes,
    hasNoData,
    dataType,
    metricType,
    metricEntities,
  } = props;

  const leftLabelRef = useRef(null);
  const rightLabelRef = useRef(null);
  const [leftLabelHeight, setLeftLabelHeight] = useState(0);
  const [rightLabelHeight, setRightLabelHeight] = useState(0);

  useEffect(() => {
    if (
      leftLabelRef &&
      leftLabelRef.current &&
      rightLabelRef &&
      rightLabelRef.current
    ) {
      setLeftLabelHeight(
        (leftLabelRef as any).current.getBoundingClientRect().height
      );
      setRightLabelHeight(
        (rightLabelRef as any).current.getBoundingClientRect().height
      );
    }
  }, [leftLabelRef, rightLabelRef]);

  // Default offset for the labels
  const yOffset = 3;
  const xOffset = -10;

  // x-axis configuration
  const timePeriod = useCallback(
    getTimeAxisConfiguration({
      period,
      isSmallScreen,
      numberOfDataPoints,
    }),
    [period, isSmallScreen, numberOfDataPoints]
  );

  const yValues = metricEntities.reduce(
    (prev: any, entity: any) => {
      const views = entity.breakdown.indexOf('views') !== -1 ? 'views' : '';
      const creations =
        entity.breakdown.indexOf('creations') !== -1 ? 'creations' : '';
      prev[views || creations] = entity.y;
      return prev;
    },
    { views: null, creations: null }
  );
  const yLeftTicks = yValues['views'] && yValues['views'].ticks(4);
  const yRightTicks = yValues['creations'] && yValues['creations'].ticks(4);
  const xTicks = x.ticks(timePeriod);
  const leftAxeOffset = 20;
  const axesBorderColor = '#d4d8dd';
  const widthLeftLabelAndAxes = 60;
  const y0 = yValues['views'] ? yValues['views'](0) : yValues['creations'](0);
  const middleLeftY = useMemo(() => sizes.height / 2 + leftLabelHeight / 2, [
    sizes.height,
    leftLabelHeight,
  ]);
  const middleRightY = useMemo(() => sizes.height / 2 + rightLabelHeight / 2, [
    sizes.height,
    rightLabelHeight,
  ]);
  const rightLabelXOffset = 8;

  return (
    <>
      <g transform={`translate(${widthLeftLabelAndAxes},0)`}>
        <line
          stroke={axesBorderColor}
          y1={margin.top}
          y2={sizes.height + margin.top}
        />
      </g>
      <HorizontalAxis transform={`translate(${margin.left}, ${margin.top})`}>
        <g key={`datetime-tick`}>
          <line
            stroke={axesBorderColor}
            strokeDasharray="1,5"
            x1={0}
            x2={sizes.width - margin.left + 20}
          />
        </g>

        {xTicks.map((tick: any) => (
          <g
            key={`datetime-tick-${tick}`}
            transform={`translate(${x(tick)},0)`}
          >
            <line stroke={axesBorderColor} y1={0} y2={sizes.height} />

            <XTickText textAnchor="middle" x={0} y={sizes.height + 24}>
              {formatDate(tick, period, isSmallScreen)}
            </XTickText>
          </g>
        ))}
      </HorizontalAxis>

      {/* Vertical text and label on left for metric type 'views' */}
      {metricType !== 'creations' && (
        <VerticalAxis
          datatype={dataType}
          transform={`translate(0, ${margin.top})`}
          isHidden={hasNoData}
        >
          <AxisLabel
            x={-middleLeftY + margin.top}
            y={8}
            isStrictDesign={true}
            ref={leftLabelRef}
          >
            Views
          </AxisLabel>

          {yLeftTicks &&
            yLeftTicks.map((tick: any, i: number) => (
              <g
                key={`tick-${tick}`}
                transform={`translate(-${leftAxeOffset},${yValues['views'](
                  tick
                )})`}
              >
                {tick !== 0 && tick !== 1 && (
                  <line
                    stroke={axesBorderColor}
                    x1={80}
                    x2={sizes.width + 38}
                  />
                )}

                {/*
                if this is a small screen, and this is either the baseline tick or a third tick, 
                display it the tick text. If this is a larger screen, always display the tick text.
              */}
                {(!isSmallScreen || i === 0 || (i + 1) % 3 === 0) && (
                  <YTickText
                    textAnchor="end"
                    x={margin.left - xOffset}
                    dy={yOffset}
                  >
                    {formatNumber(tick, dataType, true)}
                  </YTickText>
                )}
              </g>
            ))}
        </VerticalAxis>
      )}

      {/* Vertical text and label on left for metric type 'creations' */}
      {metricType !== 'views' && (
        <VerticalAxis
          datatype={dataType}
          transform={`translate(0, ${margin.top})`}
          isHidden={hasNoData}
        >
          <AxisLabel
            x={-middleRightY + margin.top}
            y={sizes.width + margin.left + rightLabelXOffset}
            isStrictDesign
            ref={rightLabelRef}
          >
            Creations
          </AxisLabel>

          {yRightTicks &&
            yRightTicks.map((tick: any, i: number) => (
              <g
                key={`tick-${tick}-right`}
                transform={`translate(${sizes.width - 15},${yValues[
                  'creations'
                ](tick)})`}
              >
                {tick !== 0 && tick !== 1 && (
                  <line
                    stroke={axesBorderColor}
                    x1={-(sizes.width - 15 - widthLeftLabelAndAxes)}
                    x2={widthLeftLabelAndAxes / 2}
                  />
                )}
                {/*
              if this is a small screen, and this is either the baseline tick or a third tick, 
              display it the tick text. If this is a larger screen, always display the tick text.
              */}
                {(!isSmallScreen || i === 0 || (i + 1) % 3 === 0) && (
                  <YTickText textAnchor="end" x={margin.left} dy={yOffset}>
                    {formatNumber(tick, dataType, true)}
                  </YTickText>
                )}
              </g>
            ))}
        </VerticalAxis>
      )}

      <AxisBaseLine
        x1={60}
        x2={sizes.width + 16}
        y1={y0 + margin.top}
        y2={y0 + margin.top}
      />

      <g transform={`translate(${sizes.width + margin.right + 5},0)`}>
        <line
          stroke={axesBorderColor}
          y1={margin.top}
          y2={sizes.height + margin.top}
        />
      </g>
    </>
  );
};

export default AxesInStrictDesign;
